
import { cashFlowData } from '../../../mocks/roadmapData';

export default function CashFlowChart() {
  const maxVal = Math.max(
    ...cashFlowData.map((d) => Math.abs(d.cost)),
    ...cashFlowData.map((d) => d.revenue),
    ...cashFlowData.map((d) => Math.abs(d.cumulative))
  );

  const barMaxHeight = 120;

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">분기별 현금흐름 추이</h2>
          <p className="text-base text-gray-500 max-w-2xl mx-auto">
            총 사업비 7,756억 집행과 1조 32억 분양 수입의 분기별 흐름을 시각화하여 자금 운용 계획을 한눈에 파악합니다.
          </p>
        </div>

        {/* Legend */}
        <div className="flex justify-center gap-8 mb-10">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-rose-400"></div>
            <span className="text-sm text-gray-600">사업비 지출</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-teal-500"></div>
            <span className="text-sm text-gray-600">분양 수입</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-3 rounded-full border-2 border-amber-500 bg-transparent"></div>
            <span className="text-sm text-gray-600">누적 손익</span>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 overflow-x-auto">
          <div className="min-w-[800px]">
            {/* Chart Area */}
            <div className="relative" style={{ height: `${barMaxHeight * 2 + 60}px` }}>
              {/* Zero Line */}
              <div className="absolute left-0 right-0 border-t-2 border-gray-300" style={{ top: `${barMaxHeight + 20}px` }}>
                <span className="absolute -left-1 -top-3 text-xs text-gray-400 font-medium">0</span>
              </div>

              {/* Bars */}
              <div className="flex items-end justify-around h-full relative">
                {cashFlowData.map((d, index) => {
                  const costHeight = (Math.abs(d.cost) / maxVal) * barMaxHeight;
                  const revenueHeight = (d.revenue / maxVal) * barMaxHeight;
                  const zeroY = barMaxHeight + 20;

                  return (
                    <div
                      key={index}
                      className="flex flex-col items-center relative group cursor-default"
                      style={{ width: `${100 / cashFlowData.length}%` }}
                    >
                      {/* Revenue bar (above zero) */}
                      <div
                        className="absolute w-5 bg-gradient-to-t from-teal-400 to-teal-600 rounded-t-md transition-all group-hover:opacity-80"
                        style={{
                          bottom: `${barMaxHeight + 40}px`,
                          height: `${revenueHeight}px`,
                          left: '50%',
                          transform: 'translateX(-60%)',
                        }}
                      ></div>

                      {/* Cost bar (below zero) */}
                      <div
                        className="absolute w-5 bg-gradient-to-b from-rose-300 to-rose-500 rounded-b-md transition-all group-hover:opacity-80"
                        style={{
                          top: `${zeroY + 2}px`,
                          height: `${costHeight}px`,
                          left: '50%',
                          transform: 'translateX(-10%)',
                        }}
                      ></div>

                      {/* Cumulative dot */}
                      <div
                        className="absolute w-3 h-3 rounded-full bg-amber-500 border-2 border-white shadow-md z-10"
                        style={{
                          left: '50%',
                          transform: 'translateX(-50%)',
                          top: `${zeroY - (d.cumulative / maxVal) * barMaxHeight}px`,
                        }}
                      ></div>

                      {/* Cumulative line to next */}
                      {index < cashFlowData.length - 1 && (
                        <svg
                          className="absolute z-5 pointer-events-none"
                          style={{
                            left: '50%',
                            top: 0,
                            width: `${100 / cashFlowData.length}%`,
                            height: '100%',
                            overflow: 'visible',
                          }}
                        >
                          <line
                            x1="0"
                            y1={zeroY - (d.cumulative / maxVal) * barMaxHeight + 6}
                            x2={`${100}%`}
                            y2={zeroY - (cashFlowData[index + 1].cumulative / maxVal) * barMaxHeight + 6}
                            stroke="#f59e0b"
                            strokeWidth="2"
                            strokeDasharray="4 2"
                          />
                        </svg>
                      )}

                      {/* Tooltip */}
                      <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity z-30 pointer-events-none">
                        <div className="bg-gray-900 text-white rounded-lg px-3 py-2 shadow-xl min-w-[150px]">
                          <div className="text-xs font-bold mb-1">{d.year} {d.quarter}</div>
                          <div className="text-xs text-rose-300">지출: {d.cost}억</div>
                          {d.revenue > 0 && <div className="text-xs text-teal-300">수입: +{d.revenue}억</div>}
                          <div className={`text-xs font-bold mt-1 ${d.cumulative >= 0 ? 'text-amber-300' : 'text-rose-300'}`}>
                            누적: {d.cumulative > 0 ? '+' : ''}{d.cumulative}억
                          </div>
                          <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-[5px] border-r-[5px] border-t-[5px] border-transparent border-t-gray-900"></div>
                        </div>
                      </div>

                      {/* Label */}
                      <div className="absolute text-xs text-gray-400 font-medium whitespace-nowrap" style={{ bottom: '0px' }}>
                        {d.quarter}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Year Labels */}
            <div className="flex mt-2 border-t border-gray-100 pt-2">
              {['2025', '2026', '2027', '2028'].map((year) => {
                const count = cashFlowData.filter((d) => d.year === year).length;
                return (
                  <div
                    key={year}
                    className="text-center text-sm font-bold text-gray-600"
                    style={{ width: `${(count / cashFlowData.length) * 100}%` }}
                  >
                    {year}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          <div className="bg-rose-50 rounded-xl p-5 border border-rose-100">
            <div className="text-sm text-rose-600 font-medium mb-1">총 사업비 지출</div>
            <div className="text-2xl font-bold text-gray-900">7,756<span className="text-sm font-medium text-gray-500">억원</span></div>
          </div>
          <div className="bg-teal-50 rounded-xl p-5 border border-teal-100">
            <div className="text-sm text-teal-600 font-medium mb-1">총 예상 수입</div>
            <div className="text-xl font-bold text-gray-900">1조 32<span className="text-sm font-medium text-gray-500">억원</span></div>
          </div>
          <div className="bg-amber-50 rounded-xl p-5 border border-amber-100">
            <div className="text-sm text-amber-600 font-medium mb-1">BEP 도달 시점</div>
            <div className="text-2xl font-bold text-gray-900">2026<span className="text-sm font-medium text-gray-500"> Q2</span></div>
          </div>
          <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-100">
            <div className="text-sm text-emerald-600 font-medium mb-1">세전 순이익</div>
            <div className="text-2xl font-bold text-gray-900">2,275<span className="text-sm font-medium text-gray-500">억원</span></div>
          </div>
        </div>
      </div>
    </section>
  );
}
